import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ErrorMessage, Field, FieldProps, Form, Formik } from "formik";
import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { useMutation } from "@tanstack/react-query";
import LoginBg from "assets/images/login-img.png";
import StereLogo from "assets/logos/StereLogo";
import { ToastCtx } from "components/Toast/ToastProvider";
import AnimatedButton from "components/custom/AnimatedButton";
import { animateRouteProps } from "constants/common";
import { registerUserApi } from "modules/Auth/api";
import { IRegisterUser, IRegisterUserResponse } from "modules/Auth/types";
import { customStylesSelector } from "store/selectors/selectors";
import { RootState } from "store/store";
import { useT } from "translation";
import { ERole } from "modules/Accounts/types";
import { COUNTRY, CountryCode } from "config";

const Register = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const isBrazilRegion = COUNTRY === CountryCode.BR;

  const { errorMessage } = useSelector((state: RootState) => state.auth);
  const { setToast } = useContext(ToastCtx);
  const validationSchema = Yup.object().shape({
    email: Yup.string().email(t("INVALID_EMAIL")).required(t("EMAIL_REQUIRED")),
    password: Yup.string()
      .min(8, t("PASSWORD_VALIDATION.length"))
      .matches(/[0-9]/, t("PASSWORD_VALIDATION.numericChar"))
      .matches(/[A-Z]/, t("PASSWORD_VALIDATION.upperCaseLetter"))
      .matches(/[a-z]/, t("PASSWORD_VALIDATION.lowerCaseLetter"))
      .matches(/[^a-zA-Z0-9]/, t("PASSWORD_VALIDATION.specialCharacter"))
      .required(t("PASSWORD_REQUIRED")),
    firstName: Yup.string().required(t("FIRST_NAME_REQUIRED")),
    lastName: Yup.string().required(t("LAST_NAME_REQUIRED")),
    ...(isBrazilRegion ? { cpf: Yup.string().required(t("CPF_IS_REQUIRED")) } : {}),
    userRole: Yup.string().required(t("USER_ROLE_IS_REQUIRED")),
  });
  const registerUserMutation = useMutation<
    IRegisterUserResponse,
    any,
    IRegisterUser
  >({
    mutationFn: registerUserApi,
    onSuccess: (registeredUser) => {
      navigate("/verifyEmail", {
        state: { registeredUser },
      });
    },
    onError: ({ error }) => {
      if (error.code === "user_not_verified") {
        return setToast({
          severity: "warning",
          text: "User is already registered please verify user to proceed",
        });
      }
      setToast({ severity: "error", text: t("UnableToRegisterUser") });
    },
  });

  const initialValues = {
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    ...(isBrazilRegion ? { cpf: "" } : {}),
    userRole: "",
  };
  const customStyles = useSelector(customStylesSelector);
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePasswordVisibility = () => setShowPassword((show) => !show);

  const handleSubmit = async (values: typeof initialValues) => {
    const userDetails = {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      password: values.password,
      meta: {
        ...(values.cpf ? { cpf: values.cpf } : {}),
        roles: values.userRole === ERole.ADMIN ? [ERole.ADMIN, ERole.AGENT] : [values.userRole]
      }
    }
    registerUserMutation.mutate(userDetails);
  };

  return (
    <Box {...animateRouteProps}>
      <Grid className="loginForm" container>
        <Grid
          item
          md={6}
          sm={7}
          xs={12}
          sx={{
            height: "100vh",
            p: "20px",
            pt: "28px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box>
            <StereLogo />
          </Box>
          <Box sx={{ mt: "24px", width: "80%" }}>
            <Typography
              sx={{
                mb: "10px",
                fontWeight: 600,
                textAlign: "center",
                fontSize: "18px",
                color: customStyles.muiTheme.palette.primary.main,
              }}
            >
              {t("REGISTER")}
            </Typography>
            <Typography
              sx={{
                mb: "16px",
                fontWeight: 400,
                textAlign: "center",
                fontSize: "14px",
                color: customStyles.muiTheme.palette.text.secondary,
              }}
            >
              {t("EnterDetails")}
            </Typography>
            <Typography
              data-cy="login-error"
              sx={{
                mb: "16px",
                fontWeight: 400,
                textAlign: "center",
                fontSize: "14px",
                color: "red",
              }}
            >
              {errorMessage}
            </Typography>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              validateOnMount={true}
              onSubmit={handleSubmit}
            >
              {({ isValid }) => (
                <Form
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box>
                    <Field
                      as={TextField}
                      type="text"
                      label={t("FIRST_NAME")}
                      name="firstName"
                      data-cy="register-full-name-input"
                      style={{
                        width: "100%",
                      }}
                      InputProps={{
                        style: {
                          borderRadius: "12px",
                        },
                      }}
                    />
                    <ErrorMessage
                      className="error-message"
                      name="firstName"
                      component="div"
                    />
                  </Box>
                  <Box
                    sx={{
                      mt: "16px",
                    }}
                  >
                    <Field
                      as={TextField}
                      type="text"
                      label={t("LAST_NAME")}
                      name="lastName"
                      data-cy="register-full-name-input"
                      style={{
                        width: "100%",
                      }}
                      InputProps={{
                        style: {
                          borderRadius: "12px",
                        },
                      }}
                    />
                    <ErrorMessage
                      className="error-message"
                      name="lastName"
                      component="div"
                    />
                  </Box>
                  {isBrazilRegion && <Box
                    sx={{
                      mt: "16px",
                    }}
                  >
                    <Field
                      as={TextField}
                      type="text"
                      label={t("CPF")}
                      name="cpf"
                      data-cy="register-full-name-input"
                      style={{
                        width: "100%",
                      }}
                      InputProps={{
                        style: {
                          borderRadius: "12px",
                        },
                      }}
                    />
                    <ErrorMessage
                      className="error-message"
                      name="cpf"
                      component="div"
                    />
                  </Box>}
                  <Box
                    sx={{
                      mt: "16px",
                    }}
                  >
                    <Field
                      as={TextField}
                      type="email"
                      label={t("EMAIL_LABEL")}
                      name="email"
                      data-cy="login-email-input"
                      style={{
                        width: "100%",
                      }}
                      InputProps={{
                        style: {
                          borderRadius: "12px",
                        },
                      }}
                    />
                    <ErrorMessage
                      className="error-message"
                      name="email"
                      component="div"
                    />
                  </Box>

                  <Tooltip
                    title={
                      <ul>
                        {[
                          t("PASSWORD_VALIDATION.length"),
                          t("PASSWORD_VALIDATION.numericChar"),
                          t("PASSWORD_VALIDATION.upperCaseLetter"),
                          t("PASSWORD_VALIDATION.lowerCaseLetter"),
                          t("PASSWORD_VALIDATION.specialCharacter"),
                        ].map((validation) => (
                          <li>{validation}</li>
                        ))}
                      </ul>
                    }
                    placement="right"
                    arrow
                  >
                    <Box
                      sx={{
                        mt: "16px",
                      }}
                    >
                      <Field
                        as={TextField}
                        type={showPassword ? "text" : "password"}
                        label={t("PASSWORD_LABEL")}
                        data-cy="login-password-input"
                        name="password"
                        style={{
                          color: "black",
                          width: "100%",
                          borderRadius: "12px",
                        }}
                        InputProps={{
                          style: {
                            borderRadius: "12px",
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePasswordVisibility}
                                edge="end"
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <ErrorMessage
                        className="error-message"
                        name="password"
                        component="div"
                      />
                    </Box>
                  </Tooltip>

                  <Box sx={{
                    mt: "16px",
                  }}>
                    <Field name="userRole">
                      {({ field }: FieldProps) => (
                        <RadioGroup
                          {...field}
                          aria-label="userRole"
                          row
                          style={{ width: "100%" }}
                          value={field.value}
                          onChange={(event) => {
                            field.onChange(event);
                          }}
                        >
                          <FormControlLabel
                            value={ERole.ADMIN}
                            control={<Radio />}
                            label={t("I_AM_ADMIN")}
                          />
                          <FormControlLabel
                            value={ERole.AGENT}
                            control={<Radio />}
                            label={t("I_AM_AGENT")}
                          />
                        </RadioGroup>
                      )}
                    </Field>
                    <ErrorMessage
                      className="error-message"
                      name="userRole"
                      component="div"
                    />
                  </Box>

                  <AnimatedButton
                    sx={{ ml: "0px", mt: "16px", textTransform: "none" }}
                    disabled={!isValid}
                    type={"submit"}
                    data_cy="login-submit-btn"
                    isLoading={registerUserMutation.isPending}
                    variant="contained"
                  >
                    {t("REGISTER")}
                  </AnimatedButton>
                  <Typography
                    variant="body1"
                    sx={{ my: "16px", textAlign: "center" }}
                  >
                    {t("OR")}
                  </Typography>
                  <Link to={"/login"}>
                    <AnimatedButton
                      variant="outlined"
                      sx={{ ml: "0px", textTransform: "none", width: "100%" }}
                    >
                      {t("SIGN_IN")}
                    </AnimatedButton>
                  </Link>
                </Form>
              )}
            </Formik>
          </Box>
        </Grid>
        <Grid
          item
          md={6}
          sm={5}
          sx={{
            height: "100vh",
            position: "relative",
          }}
        >
          <Box
            style={{
              width: "100%",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <img
              src={LoginBg}
              alt="Login Background"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <Box
              style={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                backgroundColor: customStyles.muiTheme.palette.primary.main,
                position: "absolute",
                top: 0,
                left: 0,
                opacity: 0.4,
              }}
            ></Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Register;
